import { useCities } from "@/modules/cities/components/hooks/use-cities.hook";
import { CITY_ID, loadItem, saveItem } from "helper/storage";
import { Dispatch, ReactNode, SetStateAction, createContext, useEffect, useMemo, useRef, useState } from "react";
import { getQueryParams, noop } from "../helper/utils";
import { QUERY_CITY } from "../services/web/api-cities.const";
import { City, CityResponse } from "../types";

import { useTrackedQuery } from "@/hooks/apollo-insights-hooks";
import { ViewState } from "react-map-gl/mapbox";
import { INITIAL_VIEW_STATE } from "../constants/map.const";
import { findObject } from "../helper/array";
import { useSetQueryParams } from "../hooks/use-set-query-params";

export const CityContext = createContext<{
  currentCity: City | null;
  handleSelectCity: (input: City | string) => void;
  cityList: City[];
  viewport: ViewState;
  setViewport: Dispatch<SetStateAction<ViewState>>;
  isCityListLoading: boolean;
}>({
  currentCity: null,
  handleSelectCity: noop,
  cityList: [],
  viewport: INITIAL_VIEW_STATE,
  setViewport: noop,
  isCityListLoading: false,
});

export type CityContextProviderProps = {
  children: ReactNode;
};
export type CityParamType = {
  cityID?: string;
  view?: string;
};

export const CityContextProvider = ({ children }: CityContextProviderProps) => {
  const preSelectedCityId = loadItem(CITY_ID);
  const { view, cityID } = useMemo(() => getQueryParams<CityParamType>(), []);
  const [currentCity, setCurrentCity] = useState<City | null>(null);
  const setQueryParams = useSetQueryParams();
  const currentViewport = useRef<ViewState | null>();
  const isRefresh = useRef(true);

  const [viewport, setViewport] = useState<ViewState>(INITIAL_VIEW_STATE);

  const { data: { city } = {} } = useTrackedQuery<CityResponse>(QUERY_CITY, {
    skip: !preSelectedCityId && !cityID,
    variables: { cityId: preSelectedCityId || cityID },
  });
  const { cities, loading: isCityListLoading } = useCities();

  useEffect(() => {
    if (!view || currentViewport.current) {
      return;
    }
    const arr = view.split(",");
    const latitude = Number(arr[0]);
    const longitude = Number(arr[1]);
    const zoom = Number(arr[2]);

    currentViewport.current = { ...INITIAL_VIEW_STATE, latitude, longitude, zoom };
    setViewport({ ...INITIAL_VIEW_STATE, latitude, longitude, zoom });
  }, [view]);

  useEffect(() => {
    if (city) {
      saveItem(CITY_ID, city.id);
      setCurrentCity(city);
      if (!isRefresh.current || !view) {
        setViewport((prevState) => ({
          ...prevState,
          latitude: city.position?.[1] ?? prevState.latitude,
          longitude: city.position?.[0] ?? prevState.longitude,
          zoom: INITIAL_VIEW_STATE.zoom,
        }));
      }
    }
  }, [city, cityID, view]);

  const contextValue = useMemo(() => {
    const handleSelectCity = (input: City | string) => {
      let newCity: City;
      if (typeof input === "string") {
        const fCity = findObject(cities, "id", input);
        newCity = cities[fCity];
      } else {
        newCity = input;
      }
      setQueryParams({ cityID: newCity.id });
      saveItem(CITY_ID, newCity.id);
      setCurrentCity(newCity);
      isRefresh.current = false;
      setViewport((prevState) => ({
        ...prevState,
        latitude: newCity.position?.[1] ?? prevState.latitude,
        longitude: newCity.position?.[0] ?? prevState.longitude,
        zoom: INITIAL_VIEW_STATE.zoom,
      }));
    };

    return {
      currentCity,
      handleSelectCity,
      cityList: cities,
      viewport,
      setViewport,
      isCityListLoading,
    };
  }, [currentCity, cities, viewport, setViewport, isCityListLoading, setQueryParams]);

  return <CityContext.Provider value={contextValue}>{children}</CityContext.Provider>;
};
